import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BellRing, AlertCircle, Settings, Radio } from "lucide-react";
import { Button } from "./ui/Button";

interface AnnouncementPopupDialogProps {
  id: string;
  notificationId: string;
  title: string;
  message: string;
  priority?: "low" | "medium" | "high";
  announcementTitle?: string;
  announcementContent?: string;
  isText?: boolean;
  onClose: () => void;
}

const AnnouncementPopupDialog: React.FC<AnnouncementPopupDialogProps> = ({
  id,
  notificationId,
  title,
  message,
  priority = "medium",
  announcementTitle,
  announcementContent,
  isText,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Always try to parse JSON from message field first
  let jsonTitle = "";
  let jsonContent = "";
  let jsonIsText = isText;
  
  try {
    const parsed = JSON.parse(message || "{}");
    jsonTitle = parsed.announcementTitle || "";
    jsonContent = parsed.announcementContent || "";
    // Extract isText from JSON if available
    if (parsed.isText !== undefined) {
      jsonIsText = parsed.isText;
    }
  } catch (e) {
    // If not JSON, don't use raw message - keep empty
    console.log("Failed to parse JSON in AnnouncementPopupDialog:", e);
  }

  // Use JSON parsed values if available, otherwise use props, otherwise use fallbacks
  const displayTitle = jsonTitle || announcementTitle || title || "New Announcement";
  const displayContent = jsonContent || announcementContent || "No content available";

  // Debug logging
  console.log("🔍 [AnnouncementPopupDialog] Props received:", {
    announcementTitle,
    announcementContent,
    message,
    jsonTitle,
    jsonContent,
    displayTitle,
    displayContent,
    title,
    isText: jsonIsText
  });

  useEffect(() => {
    // Add event listener to close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200); // Wait for animation to complete
  };

  const getPriorityColor = () => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50";
      case "low":
        return "text-blue-600 bg-blue-50";
      default:
        return "text-yellow-600 bg-yellow-50";
    }
  };

  const getPriorityIcon = () => {
    switch (priority) {
      case "high":
        return <AlertCircle className="h-6 w-6" />;
      case "low":
        return <Radio className="h-6 w-6" />;
      default:
        return <BellRing className="h-6 w-6" />;
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with priority indicator */}
            <div className={`p-4 ${getPriorityColor()} border-b`}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {getPriorityIcon()}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{title}</h3>
                  {priority && (
                    <p className="text-xs mt-1 opacity-80 capitalize">
                      {priority} Priority
                    </p>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="flex-shrink-0 p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4" style={{ textAlign: "left" }}>
              {/* Always show title */}
              <div>
                <h4 className="text-xl font-bold text-gray-900">
                  {displayTitle}
                </h4>
              </div>
              
              {/* Always show content */}
              <div>
                {(() => {
                  // Check if content is HTML (based on isText flag or if content starts with HTML tags)
                  const startsWithHtmlTag = /^\s*<[^>]+>/.test(displayContent);
                  
                  // If isText is false OR content starts with HTML tags, render as HTML
                  if (jsonIsText === false || startsWithHtmlTag) {
                    return (
                      <>
                        <style dangerouslySetInnerHTML={{
                          __html: `
                            .announcement-dialog-html h1 { font-weight: 700; font-size: 1.75em; color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-dialog-html h2 { font-weight: 700; font-size: 1.5em; color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-dialog-html h3 { font-weight: 700; font-size: 1.25em; color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-dialog-html h4 { font-weight: 700; font-size: 1.1em; color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-dialog-html p { margin: 0.75em 0; line-height: 1.7; }
                            .announcement-dialog-html ul, .announcement-dialog-html ol { margin: 0.75em 0; padding-left: 1.75em; }
                            .announcement-dialog-html li { margin: 0.375em 0; line-height: 1.6; }
                            .announcement-dialog-html strong { font-weight: 600; color: #374151; }
                            .announcement-dialog-html em { font-style: italic; }
                            .announcement-dialog-html a { color: #3b82f6; text-decoration: underline; }
                            .announcement-dialog-html a:hover { color: #2563eb; }
                            .announcement-dialog-html code { background: #f3f4f6; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; font-size: 0.9em; }
                          `
                        }} />
                        <div 
                          dangerouslySetInnerHTML={{ __html: displayContent }}
                          className="text-gray-700 leading-relaxed announcement-dialog-html"
                        />
                      </>
                    );
                  }
                  
                  // Otherwise render as plain text
                  return (
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {displayContent}
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-3">
              <Button
                onClick={handleClose}
                variant="outline"
                size="sm"
              >
                Dismiss
              </Button>
              <Button
                onClick={handleClose}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Got it
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementPopupDialog;

